import { useDocumentFocus } from '@/shared/hooks/useDocumentFocus';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/elements/dialog/Dialog';
import { useTranslation } from 'react-i18next';
import { Moon } from 'lucide-react';
import { globalLogout } from '@/shared/utils/logout';
import toast from 'react-hot-toast';

/**
 * InactivityDialog Component
 * Displays a dialog when the user has been inactive for too long.
 * Clicking the button logs the user out.
 *
 * Usage: place in your root component (e.g., App.tsx)
 * <InactivityDialog />
 */
export const InactivityDialog = () => {
  const { t } = useTranslation();

  const handleNavigationAttemptDuringInactivity = () => {
    // Log out user when they try to navigate during inactivity
    globalLogout('/login', false);
    toast.error(
      t(
        'focusLoss.navigationAttempted',
        'Session expired. Please log in again.'
      )
    );
  };

  const { showDialog, dismissDialog } = useDocumentFocus(
    handleNavigationAttemptDuringInactivity
  );

  const handleLogout = () => {
    dismissDialog();
    globalLogout('/login', false);
  };

  return (
    <Dialog open={showDialog} onOpenChange={() => {}}>
      <DialogContent
        hideClose
        className="max-w-md !border-0"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-amber-600 text-2xl flex items-center gap-3">
            <Moon className="h-10 w-10" />
            {t('focusLoss.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="pt-4 space-y-4">
          <p className="text-md text-gray-700">{t('focusLoss.message')}</p>

          <div className="flex justify-end">
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-black text-amber-400 font-semibold rounded hover:opacity-90 transition-opacity"
            >
              {t('focusLoss.dismiss')}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
